import { z } from 'zod';
import { type FunctionTool, tool } from '../../tool';
import { ASK_USER_QUESTION_TOOL_NAME } from './constants';
import {
  ASK_USER_QUESTION_DESCRIPTION,
  formatAskUserQuestionResult,
} from './prompts';

const QuestionOptionSchema = z
  .object({
    label: z
      .string()
      .describe(
        '该选项的显示文本，用户将看到并选择它。应简洁(1-5 个词)，并清晰描述该选择',
      ),
    description: z
      .string()
      .describe(
        '对该选项含义或选择后会发生什么的说明。用于提供关于利弊权衡或影响的上下文',
      ),
    preview: z
      .string()
      .nullable()
      .optional()
      .describe(
        '当该选项获得焦点时渲染的可选预览内容。用于线框图、代码片段或视觉对比，帮助用户比较不同选项。预期内容格式见工具描述',
      ),
  })
  .strict();

const QuestionSchema = z
  .object({
    question: z
      .string()
      .min(1)
      .describe(
        '向用户提出的完整问题。应清晰、具体，并以问号结尾。示例："我们应该使用哪个库来进行日期格式化？" 如果 multiSelect 为 true，请相应调整措辞，例如 "你希望启用哪些功能？"',
      ),
    header: z
      .string()
      .min(1)
      .max(12)
      .describe(
        `以标签/徽章形式显示的非常简短的标签（最多 12 个字符）。示例："认证方式"、"库"、"方案"。`,
      ),
    options: z
      .array(QuestionOptionSchema)
      .min(2)
      .max(4)
      .describe(
        '该问题的可选选项。必须有 2-4 个选项。每个选项应是一个独立的、互斥的选择（除非启用了 multiSelect）。不应包含"其他"选项，该选项将自动提供',
      ),
    multiSelect: z
      .boolean()
      .nullable()
      .optional()
      .describe(
        '设为 true 则允许用户选择多个选项，而非仅选一个。当各选项并非互斥时使用',
      ),
  })
  .strict();

const AnnotationSchema = z
  .object({
    preview: z
      .string()
      .nullable()
      .optional()
      .describe('所选选项的预览内容，前提是该问题使用了预览功能'),
    notes: z
      .string()
      .nullable()
      .optional()
      .describe('用户为其选择添加的自由文本备注'),
  })
  .strict();

export const AskUserQuestionInputSchema = z
  .object({
    questions: z
      .array(QuestionSchema)
      .min(1)
      .max(4)
      .describe('要向用户提出的问题（1-4 个问题）'),
    answers: z
      .record(z.string(), z.string())
      .nullable()
      .optional()
      .describe('用户界面在中断后提供的答案'),
    annotations: z
      .record(z.string(), AnnotationSchema)
      .nullable()
      .optional()
      .describe(
        '用户提供的可选按问题划分的注释（例如对预览选择的备注）。以问题文本为键。',
      ),
    metadata: z
      .object({
        source: z.string().nullable().optional(),
      })
      .nullable()
      .optional()
      .describe('用于追踪和分析目的的可选元数据。不会向用户显示'),
  })
  .strict();

export type AskUserQuestionInput = z.infer<typeof AskUserQuestionInputSchema>;
export type AskUserQuestion = FunctionTool<
  unknown,
  typeof AskUserQuestionInputSchema,
  string
>;

export function askUserQuestion(): AskUserQuestion {
  return tool({
    name: ASK_USER_QUESTION_TOOL_NAME,
    description: ASK_USER_QUESTION_DESCRIPTION,
    parameters: AskUserQuestionInputSchema,
    needsApproval: true,
    execute: async (input) =>
      formatAskUserQuestionResult({
        answers: input.answers,
        annotations: input.annotations,
      }),
  });
}
